import React from 'react';
import Typography from '@material-ui/core/Typography';
import Description from '../Description';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import i18n from 'i18n/i18n';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import CloudBrowser from 'components/CloudBrowser/CloudBrowser';
import { authenticateProvider, changeFolder, removeFolder, updateFolder } from 'redux/modules/cloudFolders';
import Grid from '@material-ui/core/Grid';
import classNames from 'classnames';
import { FolderIcon, DeleteIcon } from 'theme/icons';
import { fetchSession } from 'redux/modules/session';
import { showModal } from 'redux/modules/modal';
import { Table, TableBody, TableRow, TableCell } from '@material-ui/core';
import { DropboxIcon, GoogleDriveIcon, OneDriveIcon } from 'theme/icons';
import BaseAsset from './BaseAsset';

const localize = i18n.loc.localize;

const addProviders = [{
	type: 'dropbox',
	name: 'Dropbox',
	icon: <DropboxIcon width='43px' height='40px' />
}, {
	type: 'google',
	name: 'Google Drive',
	icon: <GoogleDriveIcon width='47px' height='40px' />
}, {
	type: 'microsoft',
	name: 'One Drive',
	icon: <OneDriveIcon width='51px' height='30px' />
}];


const styles = theme => ({
	field: {
		margin: 0
	},
	iframeClass: {
		display: 'none'
	},
	deleteButton: {
		padding: 0,
		minWidth: 20
	},
	icon: {
		margin: 0,
		cursor: 'pointer',
		color: theme.palette.colors.grey,
		'&[disabled]': {
			fill: theme.palette.action.disabled,
			pointerEvents: 'none'
		},
	},
	label: {
		marginRight: 2,
		fontSize: 14,
		lineHeight: '21px',
		color: 'rgba(40, 40, 40, 0.75)'
	},
	folder: {
		borderBottom: '0.5px solid rgba(0, 0, 0, 0.25)',
		borderTop: '0.5px solid rgba(0, 0, 0, 0.25)',
		marginTop: 1.5 * theme.spacing.unit,
		height: 60,
		display: 'flex',
		alignItems: 'center'
	},
	bold: {
		fontWeight: 'bold'
	},
	teamFolderInfo: {
		marginTop: 4 * theme.spacing.unit
	},
	providerRoot: {
		marginRight: 30
	},
	providerButton: {
		minWidth: 70,
		width: 70,
		height: 70,
		boxShadow: '1px 1px 3px rgba(0,0,0, 0.16)',
		borderRadius: 20,
		marginBottom: theme.spacing.unit
	},
	providerLabel: {
		textAlign: 'center',
		fontSize: 10,
		color: 'rgba(40, 40, 40, 0.75)'
	},
	root: {},
	sectionHeader: {
		display: 'flex',
		alignItems: 'center',
		width: 200
	},
	sectionContainer: {
		marginTop: theme.spacing.unit,
		display: 'flex'
	},
	folderIcon: {
		marginRight: theme.spacing.unit,
		color: theme.palette.colors.grey,
	},
	descriptionContainer: {
		maxWidth: 670
	},
	errorClass: {
		marginTop: 4 * theme.spacing.unit,
		fontSize: 12,
		color: '#BC1D1D !important',
		fontStyle: 'italic'
	},
	table: {
		borderTop: `1px solid ${theme.palette.colors.colorWithAlpha(theme.palette.colors.zeta, 0.15)}`
	},
	tableCell: {
		'&:first-child': {
			width: '1%' // fit to content
		},
		'&:last-child': {
			width: 50
		},
		height: 60
	},
	fullHeight: {
		height: '100%'
	}
});

@withStyles(styles)
class CloudFolder extends BaseAsset {

	constructor() {
		super();
		this.handleFolderRemove = this.handleFolderRemove.bind(this);
	}

	getProvider() {
		let provider = window.location.hash.match(/.providerid=(.+)&host/);

		return provider && decodeURIComponent(provider[1]);
	}

	componentDidMount() {
		this.getProvider();

		let providerId = this.getProvider();
		const { changeFolder, match: { params } } = this.props;


		if (providerId && (!params.tab || params.tab.toLowerCase() === 'documents')) {
			providerId = decodeURIComponent(providerId);

			changeFolder('root', providerId, {
				fileId: null,
				providerId
			});
			this.setState({ selectedFolder: 'root' + providerId });
		}
	}

	state = {}

	toggleFolder(path) {
		const state = !this.state[path];

		this.setState({
			[path]: state
		});
	}

	displayEmptyFolder(options) {
		return (<div>
			<Button
				onClick={() => this.toggleFolder(options.path)}>
				<Typography variant='body1'>{localize('documents-browse-drives')}</Typography>
			</Button>
		</div>);
	}

	displayFolder(options) {
		const { classes, cloudFolders } = this.props;
		const folder = cloudFolders[options.path];
		return (<div className={classes.folder}>
			<Typography variant='body1'>{folder.name}</Typography>
		</div>);
	}

	renderFolderSection(options) {
		const { classes, cloudFolders } = this.props;
		const folder = cloudFolders[options.path];

		return (<div>
			<div className={classes.sectionContainer}>
				<div className={classes.sectionHeader}>
					<FolderIcon className={classes.folderIcon}/><Typography variant='body1'>{options.name}</Typography>
				</div>
				{!folder && this.displayEmptyFolder(options)}
				{folder && this.displayFolder(options)}
			</div>
			{this.state[options.path] && <CloudBrowser/>}
		</div>);
	}

	handleProviderClick(type, companyId, hasDocument) {
		if (hasDocument) {
			this.setState({ hasError: true });
			return;
		}

		if (type === 'dropbox') {
			this.setState({ dropboxLogout: true, provider: 'dropbox', companyId });
			return;
		}

		authenticateProvider(type, companyId);
	}

	renderCloudProviders(document) {
		const { companyId, classes } = this.props;

		return (<Grid container>
			{addProviders.map(provider => {
				return (<div key={provider.type} className={classes.providerRoot}>
					<Button
						className={classes.providerButton}
						data-tid={`button-documents-provider-${provider.type}`}
						key={provider.type}
						onClick={() => this.handleProviderClick(provider.type, companyId, Boolean(document))}
					>
						{provider.icon}
					</Button>
					<Typography className={classes.providerLabel}>{provider.name}</Typography>
				</div>);
			})}
		</Grid>);
	}

	removeFolder({ fileId, providerId }) {
		const { companyId, removeFolder, fetchSession } = this.props;

		removeFolder({ fileId, providerId, type: 'document', companyId })
			.then(fetchSession);
	}

	renderTeamFolder(document, index) {
		if (!document) {
			return;
		}

		const { classes } = this.props;

		return (<div className={classes.teamFolderInfo}>
			<Typography style={{ paddingBottom: 10 }} className={classNames(classes.label, classes.bold)}>
				{localize('documents-your-document-folder')}
			</Typography>
			<Table className={classes.table}>
				<TableBody>
					<TableRow hover>
						<TableCell padding='none' className={classes.tableCell}>
							<FolderIcon className={classes.folderIcon}/>
						</TableCell>
						<TableCell padding='none' className={classes.tableCell}>
							<Grid
								container
								justify="space-between"
								alignContent="center"
								alignItems="center">
								{this.renderFolderInfo(document)}
								{this.renderEditFolderField(document)}
								<Grid item>
									<Grid container className={classes.fullHeight} alignContent="center">
										{this.renderRenameButton(document)}
										{this.renderSaveButton(document)}
									</Grid>
								</Grid>
							</Grid>
						</TableCell>
						<TableCell align='right' padding='none' className={classes.tableCell}>
							<IconButton
								color='secondary'
								data-tid="button-documents-folder-delete"
								component='span'
								aria-label='Delete'
								onClick={() => this.handleFolderRemove(document)}
							>
								<DeleteIcon/>
							</IconButton>
						</TableCell>
					</TableRow>
				</TableBody>
			</Table>
		</div>);
	}

	handleFolderRemove(document) {
		this.props.showModal('CONFIRMATION_MODAL', {
			message: localize('documents-remove-folder-message', document.displayName),
			buttons: [
				<Button
					data-tid="button-documents-delete-cancel"
					key='cancel-folder-button'
					variant='outlined'
					color='primary'
				>
					{localize('cancel')}
				</Button>,
				<Button
					key='delete-folder-button'
					data-tid="button-documents-delete-confirm"
					variant='contained'
					color='primary'
					onClick={() => this.removeFolder(document)}
				>
					{localize('documents-remove-folder-cta')}
				</Button>
				
			]
		});
	}

	handleOnLoad() {
		const { companyId, provider } = this.state;

		setTimeout(() => authenticateProvider(provider, companyId), 500);
	}

	dropboxLogout() {
		const { dropboxLogout } = this.state;
		const { classes: { iframeClass } } = this.props;

		if (!dropboxLogout) {
			return;
		}

		return (<iframe src="https://www.dropbox.com/logout" className={iframeClass} onLoad={this.handleOnLoad()}/>);
	}

	renderError() {
		const { hasError } = this.state;
		const { classes, company: { folders } } = this.props;

		if (!hasError || folders.document.length === 0) {
			return;
		}

		return (
			<Typography className={classes.errorClass} dangerouslySetInnerHTML={{__html: localize('documents-error-limited')}} />
		);
	}

	renderBasicView(index) {
		const { classes, company: { folders } } = this.props;

		return (<div>
			<div className={classes.descriptionContainer}>
				<Description dangerousInnerHTML={localize('documents-description')} />
			</div>
			{this.renderCloudProviders(folders.document && folders.document[0])}
			{this.renderError()}
			{this.renderTeamFolder(folders.document && folders.document[0])}
			{this.dropboxLogout()}
		</div>);
	}

	render() {
		const { classes, match } = this.props;
		const { selectedFolder } = this.state;
		const displayCloudBrowser = Boolean(this.getProvider());

		return (<div className={classes.root}>
			{!displayCloudBrowser && this.renderBasicView()}

			{displayCloudBrowser && <CloudBrowser selectedFolder={selectedFolder} type="document" match={match}/>}
		</div>);
	}

}

const mapStateToProps = state => ({
	cloudFolders: {
		...state.cloudFolders,
		documentFolder: null,
		imageFolder: null
	},
	company: state.companies.rawDataById[state.companies.selectedId] || {},
	companyId: state.companies.selectedId,
});

const mapDispatchToProps = dispatch => bindActionCreators({
	fetchSession,
	changeFolder,
	updateFolder,
	removeFolder,
	showModal
}, dispatch);

CloudFolder.propTypes = {
	updateFolder: PropTypes.func,
	fetchSession: PropTypes.func,
	removeFolder: PropTypes.func,
	changeFolder: PropTypes.func,
	showModal: PropTypes.func,
	classes: PropTypes.object,
	cloudFolders: PropTypes.object,
	company: PropTypes.object,
	companyId: PropTypes.string,
	match: PropTypes.object.isRequired
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(CloudFolder);



// WEBPACK FOOTER //
// ./src/components/TeamFolders/Documents.js